// Contexto de Autenticação usando Supabase Auth
import { createContext, useContext, useEffect, useState, useRef } from 'react';
import type { ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { getCurrentSession } from '../services/authService';
import type { User as AppUser } from '../services/authService';
import type { AuthSession } from '@supabase/supabase-js';

interface AuthContextType {
  user: AppUser | null;
  session: AuthSession | null;
  loading: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null);
  const [session, setSession] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);
  const hasInitializedRef = useRef(false);

  // Função para atualizar estado de autenticação
  const updateAuthState = async (newSession: AuthSession | null, shouldSetLoading = false) => {
    try {
      if (shouldSetLoading) {
        setLoading(true);
      }
      
      if (newSession && newSession.user) {
        console.log('[AuthContext] Atualizando estado com sessão e usuário');
        setSession(newSession);
        
        // Usar o usuário da sessão diretamente (mais confiável)
        const sessionUser = newSession.user;
        const appUser: AppUser = {
          id: sessionUser.id,
          email: sessionUser.email || '',
          nome: sessionUser.user_metadata?.nome || '',
          avatar: sessionUser.user_metadata?.avatar || undefined,
        };
        setUser(appUser);
        console.log('[AuthContext] Usuário setado:', appUser.email);
      } else {
        console.log('[AuthContext] Limpando estado (sem sessão)');
        setSession(null);
        setUser(null);
      }
    } catch (error) {
      console.error('[AuthContext] Erro ao atualizar estado de autenticação:', error);
      setSession(null);
      setUser(null);
    } finally {
      // Garantir que loading sempre seja false após qualquer atualização
      // IMPORTANTE: Isso é crítico para o redirecionamento funcionar
      setLoading(false);
      console.log('[AuthContext] updateAuthState concluído - loading: false');
    }
  };

  // Carregar sessão inicial e configurar listener
  useEffect(() => {
    let mounted = true;
    let timeoutId: number;
    let hasFinished = false;

    // Timeout de segurança: se após 3 segundos não carregou, parar loading
    timeoutId = setTimeout(() => {
      if (mounted && !hasFinished) {
        console.warn('[AuthContext] Timeout ao carregar sessão inicial - desativando loading');
        setLoading(false);
        hasFinished = true;
      }
    }, 3000);

    // Carregar sessão inicial
    const initAuth = async () => {
      try {
        console.log('[AuthContext] Iniciando carregamento de sessão...');
        const currentSession = await getCurrentSession();
        console.log('[AuthContext] Sessão obtida:', currentSession ? 'existe' : 'não existe');
        
        if (mounted && !hasFinished) {
          await updateAuthState(currentSession);
          hasInitializedRef.current = true;
          hasFinished = true;
          clearTimeout(timeoutId);
          console.log('[AuthContext] Inicialização concluída');
        }
      } catch (error) {
        console.error('[AuthContext] Erro ao inicializar:', error);
        if (mounted && !hasFinished) {
          setSession(null);
          setUser(null);
          setLoading(false);
          hasInitializedRef.current = true;
          hasFinished = true;
          clearTimeout(timeoutId);
        }
      }
    };

    // Iniciar autenticação imediatamente
    initAuth();

    // Observar mudanças na autenticação (apenas após inicialização)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        console.log('[AuthContext] Evento recebido:', event, 'Sessão:', newSession ? 'existe' : 'não existe');
        
        // Ignorar INITIAL_SESSION completamente - já inicializamos manualmente
        if (event === 'INITIAL_SESSION') {
          console.log('[AuthContext] Ignorando INITIAL_SESSION - já inicializado manualmente');
          return;
        }

        // Processar SIGNED_IN e SIGNED_OUT mesmo durante inicialização (para casos de login rápido)
        if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
          console.log('[AuthContext] Processando evento crítico:', event);
          console.log('[AuthContext] Sessão recebida:', newSession ? 'existe' : 'não existe');
          console.log('[AuthContext] Usuário na sessão:', newSession?.user?.email || 'não existe');
          
          if (mounted) {
            await updateAuthState(newSession, false);
            // Garantir que loading seja false após atualizar o estado (crítico para redirecionamento)
            setLoading(false);
            // Se foi SIGNED_IN e ainda não inicializamos, marcar como inicializado
            if (event === 'SIGNED_IN' && !hasInitializedRef.current) {
              hasInitializedRef.current = true;
              hasFinished = true;
              clearTimeout(timeoutId);
            }
            console.log('[AuthContext] Estado atualizado após evento:', event, 'loading: false');
          }
          return;
        }

        // Ignorar outros eventos se ainda não inicializamos
        if (!hasInitializedRef.current) {
          console.log('[AuthContext] Ignorando evento - ainda inicializando:', event);
          return;
        }

        console.log('[AuthContext] Processando evento de autenticação:', event);
        
        if (mounted) {
          await updateAuthState(newSession, false);
        }
      }
    );

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      setSession(null);
      setUser(null);
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
      throw error;
    }
  };

  const refreshSession = async () => {
    try {
      console.log('[AuthContext] refreshSession chamado');
      // Não setar loading como true aqui para não interferir com o fluxo de login
      // O listener do Supabase já atualiza o estado automaticamente
      
      // Buscar sessão atual do Supabase
      const currentSession = await getCurrentSession();
      console.log('[AuthContext] Sessão obtida:', currentSession ? 'existe' : 'não existe');
      
      if (currentSession && currentSession.user) {
        // Usar o usuário da sessão diretamente
        const sessionUser = currentSession.user;
        const appUser: AppUser = {
          id: sessionUser.id,
          email: sessionUser.email || '',
          nome: sessionUser.user_metadata?.nome || '',
          avatar: sessionUser.user_metadata?.avatar || undefined,
        };
        
        setSession(currentSession);
        setUser(appUser);
        console.log('[AuthContext] Usuário setado:', appUser.email);
        // Garantir que loading seja false após atualizar
        setLoading(false);
      } else {
        setSession(null);
        setUser(null);
        setLoading(false);
        console.log('[AuthContext] Nenhuma sessão válida encontrada');
      }
      
      console.log('[AuthContext] refreshSession concluído');
    } catch (error) {
      console.error('[AuthContext] Erro ao atualizar sessão:', error);
      setSession(null);
      setUser(null);
      setLoading(false);
    }
  };

  const isAuthenticated = !!session && !!user;
  
  // Log para debug - verificar quando isAuthenticated muda
  useEffect(() => {
    console.log('[AuthContext] isAuthenticated mudou:', { 
      isAuthenticated, 
      hasSession: !!session, 
      hasUser: !!user,
      userEmail: user?.email 
    });
  }, [isAuthenticated, session, user]);

  const value: AuthContextType = {
    user,
    session,
    loading,
    isAuthenticated,
    signOut,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}

